import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { URL } from "../store/utils";
import ServerMaintenancePage from "../Pages/ServerMaintenancePage";
import OfflinePage from "../Pages/OfflinePage";

const ServerStatusContext = createContext({
  serverDown: false,
  offline: false,
});

// URL.url sonunda slash içermeyebilir (fallback "https://bayineriot.com",
// dev "http://localhost:5002"). Düz birleştirme "...comhealth" gibi bozuk bir
// host üretir → /health probe'u KALICI olarak hataya düşer, `recover()` hiçbir
// zaman `res.ok` göremez ve overlay bağlantı geri gelse de bir daha kapanmaz.
// Bu yüzden base'i slash-güvenli birleştiriyoruz.
export const buildHealthUrl = (base) => `${String(base).replace(/\/+$/, "")}/health`;

const HEALTH_URL = buildHealthUrl(URL.url);
const POLL_INTERVAL_MS = 5000;
const INTERNET_CHECK_URL = "https://www.google.com/generate_204";

export const useServerStatus = () => useContext(ServerStatusContext);

async function hasInternet() {
  if (!navigator.onLine) return false;
  try {
    await fetch(INTERNET_CHECK_URL, {
      method: "HEAD",
      mode: "no-cors",
      cache: "no-store",
      signal: AbortSignal.timeout(3000),
    });
    return true;
  } catch {
    return false;
  }
}

export const ServerStatusProvider = ({ children }) => {
  const [serverDown, setServerDown] = useState(false);
  const [offline, setOffline] = useState(false);
  // serverDown state'ini poll içinde stale-closure olmadan okuyabilmek için ref ayna
  const serverDownRef = useRef(false);
  const pollingRef = useRef(null);

  const setDown = useCallback((v) => {
    serverDownRef.current = v;
    setServerDown(v);
  }, []);

  const clearPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  const recover = useCallback(() => {
    clearPolling();
    window.location.reload();
  }, [clearPolling]);

  // Bakımda ekranını göstermenin/gizlemenin TEK otoritesi: /health probe'u.
  // Hata event'leri (FETCH_ERROR / 502 / 503) yalnızca bu kontrolü tetikler;
  // ekranı doğrudan açmazlar. Böylece cihaz kaynaklı geçici hatalar (örn. 504)
  // veya tekil ağ blipleri yanlışlıkla "bakımda" göstermez.
  const pollCheck = useCallback(async () => {
    const internetOk = await hasInternet();

    if (!internetOk) {
      setOffline(true);
      setDown(false);
      return;
    }

    setOffline(false);

    try {
      const res = await fetch(HEALTH_URL, {
        method: "GET",
        cache: "no-store",
        signal: AbortSignal.timeout(4000),
      });
      if (res.ok) {
        if (serverDownRef.current) {
          recover(); // gerçekten down idi, toparlandı → yeniden yükle
        } else {
          clearPolling(); // yanlış alarm — sunucu sağlıklı, overlay açma
        }
      } else {
        setDown(true);
      }
    } catch {
      setDown(true);
    }
  }, [recover, clearPolling, setDown]);

  const startPolling = useCallback(() => {
    if (pollingRef.current) return;
    pollingRef.current = setInterval(pollCheck, POLL_INTERVAL_MS);
  }, [pollCheck]);

  useEffect(() => {
    const handleUnreachable = () => {
      // Karar SADECE /health poll'una ait — event yalnızca kontrolü başlatır
      startPolling();
      pollCheck();
    };

    const handleOnline = () => {
      pollCheck();
    };

    const handleOffline = () => {
      setOffline(true);
      setDown(false);
      startPolling();
    };

    window.addEventListener("server-unreachable", handleUnreachable);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("server-unreachable", handleUnreachable);
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      clearPolling();
    };
  }, [startPolling, pollCheck, clearPolling, setDown]);

  const showOverlay = serverDown || offline;

  return (
    <ServerStatusContext.Provider value={{ serverDown, offline }}>
      {showOverlay && (offline ? <OfflinePage /> : <ServerMaintenancePage />)}
      {children}
    </ServerStatusContext.Provider>
  );
};

export default ServerStatusContext;
